// Legacy step strings read into step-action values. Input is grammar 2: store reads upgrade grammar 1 envelopes
// first (template-envelope.ts, variable-grammar-v1.heuristics.ts).
import type { ComponentTarget } from '../stable-surfaces/target.ts'
import type { DefinitionId, SurfaceId } from '../stable-surfaces/id.ts'
import { flatComponentTarget, stepControl, stepSurface } from '../stable-surfaces/step-ref.ts'
import { parse } from '../journey/template.ts'
import { canonicalJourneyVariableName } from '../journey/variable-name.ts'
import { bindText, hasGenerator } from './bind.ts'
import { keyChordFaultText, keyChordTextRunText, readKeyChord, textRun } from './key-chord.ts'
import { legacyReadLocator } from './legacy-locator.ts'
import { LegacyReadError } from './legacy-read-error.ts'
import { legacyProseDestination } from './legacy-navigate.heuristics.ts'
import {
  DRAG_DIRECTION,
  type ComponentRef,
  type DragStepAction,
  type FilesStepAction,
  type KeyChord,
  type KeyEvent,
  type NavigateStepAction,
  type PressKeyStepAction,
  type ReadVariableStepAction,
  type SelectStepAction,
  type TargetRef,
  type TextEntryStepAction,
} from './schema.ts'
import type { GeneratorKind, Text, TextPart, VarName } from './text.ts'

/** The legacy fields of a `type` / `input` step. */
export interface LegacyTextEntry {
  action: string
  value: string
  append?: boolean
  variable?: string
}

export function legacyReadTextEntry(type: TextEntryStepAction['type'], step: LegacyTextEntry): TextEntryStepAction {
  const store = legacyStore(step.variable)
  return {
    type,
    action: step.action,
    input: legacyReadText(step.value),
    ...(step.append === undefined ? {} : { append: step.append }),
    ...(store ? { store } : {}),
  }
}

/** The legacy fields of a step whose arm reads `value` alone. */
export interface LegacyValueStep {
  action: string
  value?: string
}

const LEGACY_KEY_EVENT: Record<'keydown' | 'keyup', KeyEvent> = { keydown: 'down', keyup: 'up' }

// `value` is a whitespace-separated SEQUENCE of chords pressed in order, so one step can perform a compound
// operation (`Control+A Delete` clears a field). No value presses Enter; a value of only whitespace is the space key.
export function legacyReadPressKey(step: LegacyValueStep & { keyboardEvent?: 'keydown' | 'keyup' }): PressKeyStepAction {
  const value = step.value ?? ''
  const chords = bindText(legacyReadTemplate(value.trim()), new Map(), (name) => {
    throw new LegacyReadError('keys', keyChordFaultText({ kind: 'reference', text: `$${name}` }))
  }).split(/\s+/).filter(Boolean)
  return {
    type: 'pressKey',
    action: step.action,
    keys: chords.length ? chords.map(legacyReadKeyChord) : [{ modifiers: [], key: value ? 'Space' : 'Enter' }],
    ...(step.keyboardEvent ? { event: LEGACY_KEY_EVENT[step.keyboardEvent] } : {}),
  }
}

/** Why a written pressKey's stored `value` reads as no chord sequence, or as a press of text (`textRun`), or null (another
 *  type, or a readable value): the write-time refusal of its `keys` reading. A held key event is no text entry. */
export function legacyPressKeyRefusal(step: { type: string; action: string; value?: unknown; keyboardEvent?: 'keydown' | 'keyup' }): string | null {
  if (step.type !== 'pressKey') return null
  let read: PressKeyStepAction
  try {
    read = legacyReadPressKey({ action: step.action, ...(typeof step.value === 'string' ? { value: step.value } : {}), ...(step.keyboardEvent ? { keyboardEvent: step.keyboardEvent } : {}) })
  } catch (error) {
    if (error instanceof LegacyReadError) return error.message
    throw error
  }
  const run = read.event ? null : textRun(read.keys)
  return run === null ? null : keyChordTextRunText(run)
}

/** The chords a step presses, or null: another type, a held key event, or a value naming no chords. */
export function legacyPressedChords(step: { type: string; action: string; value?: unknown; keyboardEvent?: 'keydown' | 'keyup' }): KeyChord[] | null {
  if (step.type !== 'pressKey' || step.keyboardEvent) return null
  try {
    return legacyReadPressKey({ action: step.action, ...(typeof step.value === 'string' ? { value: step.value } : {}) }).keys
  } catch (error) {
    if (error instanceof LegacyReadError) return null
    throw error
  }
}

function legacyReadKeyChord(text: string): KeyChord {
  const read = readKeyChord(text)
  if ('fault' in read) throw new LegacyReadError('keys', keyChordFaultText(read.fault))
  return read.chord
}

export function legacyReadSelect(step: LegacyValueStep): SelectStepAction {
  return { type: 'select', action: step.action, option: legacyReadTemplate(step.value ?? '') }
}

export function legacyReadFiles(type: FilesStepAction['type'], step: LegacyValueStep): FilesStepAction {
  return { type, action: step.action, files: splitText(legacyReadTemplate((step.value ?? '').trim()), FILE_SLUG_SEPARATOR) }
}

// The legacy file `value` (and an attach decision's `file`): comma-separated slugs.
const FILE_SLUG_SEPARATOR = /\s*,\s*/

/** The text naming `slugs`. */
export function fileSlugList(slugs: readonly string[]): string {
  return slugs.join(', ')
}

/** The slugs a plain `fileSlugList` text names. */
export function fileSlugs(text: string): string[] {
  return text.trim().split(FILE_SLUG_SEPARATOR).filter(Boolean)
}

// `t` cut at each `separator` match inside its text parts; empty pieces dropped. A reference never spans a cut.
function splitText(t: Text, separator: RegExp): Text[] {
  const pieces: Text[] = [[]]
  for (const part of t) {
    if (!('text' in part)) {
      pieces[pieces.length - 1]!.push(part)
      continue
    }
    part.text.split(separator).forEach((text, i) => {
      if (i > 0) pieces.push([])
      if (text) pieces[pieces.length - 1]!.push({ text })
    })
  }
  return pieces.filter((piece) => piece.length)
}

/** The legacy target fields of a step. At execution `selector` is the run's resolution of a `component`
 *  (resolveActionControl), so it is the target the run executes. */
export interface LegacyTarget {
  selector?: string
  component?: ComponentTarget
}

/** Every element-identity field a stored step may carry. `label`, `inRunAnchors` and `abstractTarget` are not identity:
 *  provenance, observation anchors and abstract evidence (docs/step-action/model.md). */
export interface LegacyStepTargetFields extends LegacyTarget {
  surfaceId?: SurfaceId
  controlId?: DefinitionId
  controlName?: string
}

/** A recorded control: the component id `controlId` (the `component`'s, else the step's own) and the control's display
 *  `name`. With no component path it is the step's Sightmap identity (read compatibility for steps bound before paths);
 *  beside one it is the run's record of the control the path resolved to (resolveActionControl). */
export interface LegacyControlRef {
  controlId: DefinitionId
  name?: string
}

/** A stored step's target encodings, each read once. */
export interface LegacyStepTarget {
  /** The Surface the target lives on: the component's, else the step's own. */
  surfaceId?: SurfaceId
  /** The Sightmap address, when it carries a component path. */
  component?: ComponentTarget & { path: NonNullable<ComponentTarget['path']> }
  control?: LegacyControlRef
  /** The stored selector: the authored witness, or at execution the run's resolution of `component` / `control`. */
  selector?: string
}

// TODO(step-action typed storage, docs/step-action/model.md): delete once stored steps carry one `TargetRef` and stored data is migrated.
export function legacyReadStepTarget(step: LegacyStepTargetFields): LegacyStepTarget {
  const { component } = step
  const path = component?.path?.length ? component.path : undefined
  const surfaceId = stepSurface(step)
  const controlId = stepControl(step)
  return {
    ...(surfaceId ? { surfaceId } : {}),
    ...(component && path ? { component: { ...component, path } } : {}),
    ...(controlId ? { control: { controlId, ...(step.controlName ? { name: step.controlName } : {}) } } : {}),
    ...(step.selector ? { selector: step.selector } : {}),
  }
}

/** Whether the step names an element by any encoding. */
export function namesElement(target: LegacyStepTarget): boolean {
  return !!(target.component || target.control || target.selector)
}

/** The Sightmap address of the target: its component path, else its recorded control as a flat address. */
export function sightmapAddress(target: LegacyStepTarget): ComponentTarget | undefined {
  if (target.component) return target.component
  return flatComponentTarget({ surfaceId: target.surfaceId, controlId: target.control?.controlId })
}

/** The target a run executes: its `selector`, else its Sightmap `component` path; null when it names neither. */
export function legacyReadTarget(step: LegacyTarget): TargetRef | null {
  const target = legacyReadStepTarget(step)
  if (target.selector) return { locator: legacyReadLocator(target.selector) }
  return target.component ? { component: legacyComponentRef(target.component) } : null
}

// A param value is a template unless the instance was observed (`paramsSource: 'observation'`): then it is literal.
function legacyComponentRef(target: NonNullable<LegacyStepTarget['component']>): ComponentRef {
  const read = target.paramsSource === 'observation' ? literalText : legacyReadTemplate
  return {
    surfaceId: target.surfaceId,
    path: target.path.map(({ componentId, params }) => ({
      componentId,
      ...(params ? { params: Object.fromEntries(Object.entries(params).map(([name, value]) => [name, read(value)])) } : {}),
    })),
    ...(target.selector ? { selector: target.selector } : {}),
  }
}

/** The legacy fields of a `drag` / `dragTo` step: `variable` + `context` are the hold-and-read preview. */
export interface LegacyDrag extends LegacyValueStep {
  context?: string
  variable?: string
  destination?: ComponentTarget
}

export function legacyReadDrag(type: DragStepAction['type'], step: LegacyDrag): DragStepAction {
  const store = legacyStore(step.variable)
  return {
    type,
    action: step.action,
    dest: legacyDragDestination(step.value ?? '', step.destination),
    ...(store && step.context ? { preview: { list: { locator: legacyReadLocator(step.context) }, store } } : {}),
  }
}

// A direction with an amount (`left:620`) has the direction form but no meaning: a nudge is one slot, never a distance.
// It holds no `$`, so its stored text is its decoded text.
const DIRECTION_WITH_AMOUNT = new RegExp(`^(${DRAG_DIRECTION.options.join('|')})\\s*:\\s*-?\\d+(\\.\\d+)?(px)?$`, 'i')

// `value` names the drop target's selector (the run writes a Sightmap `destination`'s resolution there), or a
// direction; neither: the `destination`, else down.
function legacyDragDestination(value: string, destination: ComponentTarget | undefined): DragStepAction['dest'] {
  const dest = value.trim()
  const direction = DRAG_DIRECTION.options.find((d) => d === dest.toLowerCase())
  if (direction) return { direction }
  if (DIRECTION_WITH_AMOUNT.test(dest)) {
    throw new LegacyReadError('destination', `drag: "${dest}" is not a drop destination — name the drop target's selector, or one of ${DRAG_DIRECTION.options.join(' / ')} to move the item one slot`)
  }
  const target = legacyReadTarget({ selector: dest, component: destination })
  return target ? { target } : { direction: 'down' }
}

/** The legacy fields of a `read_variable` step. */
export interface LegacyReadVariable extends LegacyStepTargetFields {
  action: string
  value?: string
  variable?: string
  attribute?: string
  readFrom?: 'clipboard' | 'opfs'
}

// Generator expressions and a literal page-level value (e.g. the current URL, which no DOM element holds) are
// bindings, not page reads. An `attribute` alone reads the page body's attribute. No `variable`: nothing to store.
export function legacyReadReadVariable(step: LegacyReadVariable): ReadVariableStepAction | null {
  const store = legacyStore(step.variable)
  const base = { type: 'read_variable', action: step.action } as const
  if (step.readFrom) {
    const opfs = step.readFrom === 'opfs'
    // Each fault named on its own, so a missing variable does not read as a call to add a control.
    const invalid = (fault: string) => new LegacyReadError('readFrom',
      `${opfs ? 'OPFS' : 'Clipboard'} capture ${fault}${opfs ? ' (value: the file path to read, or none to list every file)' : ''}`)
    if (!store) throw invalid('needs `variable`, the name to store the capture into')
    if (namesElement(legacyReadStepTarget(step)) || step.attribute) throw invalid('reads browser state, not an element — omit control, target and attribute')
    if (!opfs) {
      if (step.value !== undefined) throw invalid('takes no value')
      return { ...base, store, from: { clipboard: true } }
    }
    const path = legacyReadText((step.value ?? '').trim())
    return { ...base, store, from: { opfs: path.length ? { path } : {} } }
  }
  if (!store) return null
  const value = legacyReadText(step.value ?? '')
  if (hasGenerator(value)) return { ...base, store, from: { value } }
  const element = legacyReadTarget(step)
  if (element || step.attribute) {
    return { ...base, store, from: { element: element ?? { locator: { raw: 'body' } }, ...(step.attribute ? { attribute: step.attribute } : {}) } }
  }
  if (value.length) return { ...base, store, from: { value } }
  throw new LegacyReadError('selector', `read_variable: no element to read for "${store}" — resolve the step to the control that carries the value, use readFrom: clipboard for copied text, or supply value only when capturing a known page-level value such as the current URL`)
}

/** A navigate's destination: its `value`, else a URL or path its prose names; null when neither names one. */
export function legacyNavigateUrl(step: LegacyValueStep): Text | null {
  const value = (step.value ?? '').trim()
  return value ? legacyReadTemplate(value) : legacyProseDestination(legacyReadTemplate(step.action))
}

// A navigate with no destination must not pass as a no-op: the run would read the page it never left as the
// destination (missing-bug eval-msz3pj39).
export function legacyReadNavigate(step: LegacyValueStep): NavigateStepAction {
  const url = legacyNavigateUrl(step)
  if (!url) throw new LegacyReadError('destination', 'navigate requires a destination — put the URL to navigate to in the action value')
  return { type: 'navigate', action: step.action, url }
}

/** A grammar 2 template with no generators (a selector, URL, option or prose): `$name` a reference. */
export function legacyReadTemplate(value: string): Text {
  return parse(value, 'value').segments.map((segment) => typeof segment === 'string' ? { text: segment } : { var: segment.ref })
}

const literalText = (text: string): Text => text ? [{ text }] : []

function legacyStore(variable: string | undefined): VarName | null {
  return variable ? canonicalJourneyVariableName(variable) : null
}

/** A legacy generator expression (`random()` / `timestamp()`) as its generator; null for any other text. */
function legacyGenerator(expression: string): GeneratorKind | null {
  if (expression === 'random()') return 'random'
  if (expression === 'timestamp()') return 'timestamp'
  return null
}

/** A grammar 2 template value: `$name` a reference, an embedded `${random()}` / `${timestamp()}` (stored `$${…}`) a generator. */
export function legacyReadText(value: string): Text {
  return wholeGeneratorOr(value, text => legacyReadTemplate(text).flatMap(part => 'text' in part ? generatorParts(part.text) : [part]))
}

/** A variable `init`: literal text (never a template) with embedded generators; null when it holds no value (empty, or the variable's own `$name`). */
export function legacyReadInit(name: VarName, init: string | undefined): Text | null {
  return !init || init === `$${name}` ? null : wholeGeneratorOr(init, generatorParts)
}

/** Whether a legacy value holds a generator; references are not parsed, so any text is accepted. */
export function legacyHasGenerator(value: string): boolean {
  return hasGenerator(wholeGeneratorOr(value, generatorParts))
}

// A whole-value generator expression is one generator; any other value is read by `read`.
function wholeGeneratorOr(value: string, read: (value: string) => Text): Text {
  const whole = legacyGenerator(value)
  return whole ? [{ generate: whole }] : read(value)
}

// An embedded generator expression (`qa-${random()}@audos.com`, Bug Report #20814).
const EMBEDDED_GENERATOR = /\$\{\s*(random\(\)|timestamp\(\))\s*\}/g

function generatorParts(text: string): TextPart[] {
  const parts: TextPart[] = []
  let end = 0
  for (const match of text.matchAll(EMBEDDED_GENERATOR)) {
    if (match.index > end) parts.push({ text: text.slice(end, match.index) })
    parts.push({ generate: legacyGenerator(match[1]!)! })
    end = match.index + match[0].length
  }
  if (end < text.length) parts.push({ text: text.slice(end) })
  return parts
}
